import { LoadingIndicator, BusyContent } from '../components/ui/Primitives';
import { trackMetaEvent } from '../utils/metaPixel';
import React, { useState, useEffect } from 'react';
import "./SignUp.css"
import { useNavigate, useLocation } from 'react-router-dom';
import flashMessage from '../components/HelperFunctions';
import { logEvent } from '../utils/logger';
import { validateCompInvitation } from '../services/compInvitationApi';
import PasswordFields from '../components/PasswordFields';
import { passwordPolicyError } from '../utils/passwordPolicy';
import GoogleSignInButton from '../components/GoogleSignInButton';


function SignUp({ checkLogin, orgMember, login }) {
  const capVal = null;
  const [buttonClicked, setButtonClicked] = useState(false);  // New state variable for button click status
  const [newEmail, setNewEmail] = useState("");
  const [subscriptionType, setSubscriptionType] = useState(null);
  const [compInviteToken, setCompInviteToken] = useState('');
  const [compInvitation, setCompInvitation] = useState(null);
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteError, setInviteError] = useState('');
  const [password, setPassword] = useState('');
  const [verifyPassword, setVerifyPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const location = useLocation();


  const handleSubmit = async (e) => {
    e.preventDefault();

    if (inviteLoading || inviteError) return;
    setButtonClicked(true);

    // Extract form values
    const email = document.getElementById("signup-email").value;
    const firstName = document.getElementById("first-name").value;
    const lastName = document.getElementById("last-name").value;


    // Validate input here if needed
    if (!email || !password || !verifyPassword || !firstName || !lastName) {
      flashMessage('error', 'Please fill in all required fields')
      setButtonClicked(false); // Reset button state after operation
      return;
    }

    const policyError = passwordPolicyError(password);
    if (policyError) {
      setPasswordError(policyError);
      flashMessage('error', policyError);
      setButtonClicked(false);
      return;
    }
    if (password !== verifyPassword) {
      flashMessage('error', 'Passwords do not match.');
      setButtonClicked(false);
      return;
    }


    // Send data to the server
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_HOST}/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          password,
          verifyPassword,
          firstName,
          lastName,
          capVal,
          subscriptionType,
          pricingVersion: location.state?.pricingVersion,
          compInviteToken: compInviteToken || undefined,
        })
      });

      if (response.ok) {
        trackMetaEvent('SignupSubmitted', {}, true);

        const event = {
          event: {
            label: 'Click',
            data: {
              value: 'SignUp Page Submitted',
              email: email,
              billingAccess: compInviteToken ? 'comped' : 'standard',
            }
          }

        }

        logEvent(event);

        navigateToVerifyAccount();
      } else {
        const responseBody = await response.json().catch(() => ({}));
        const serverMessage = responseBody?.error?.message;
        if (/commonly used|data breach/i.test(serverMessage || '')) {
          setPasswordError(serverMessage);
        }
        if (response.status === 409 && !serverMessage) {
          flashMessage('error', 'Email already exists');
        } else {
          flashMessage('error', serverMessage || 'An error occurred. Please try again later');
        }
      }
    } catch (_error) {
      flashMessage('error', 'Sayable could not create the account. Please try again.');
    } finally {
      setButtonClicked(false);
    }
  }

  const navigate = useNavigate();

  const navigateToVerifyAccount = () => {
    navigate('/verify-account');
  };


  useEffect(() => {
    let cancelled = false;
    const urlParams = new URLSearchParams(location.search);
    const inviteToken = urlParams.get('invite') || '';
    const emailParam = urlParams.get('email');
    if (inviteToken) {
      setCompInviteToken(inviteToken);
      setInviteLoading(true);
      setInviteError('');
      validateCompInvitation(inviteToken)
        .then((invitation) => {
          if (cancelled) return;
          setCompInvitation(invitation);
          setSubscriptionType(invitation.subscriptionType);
          if (invitation.email) setNewEmail(invitation.email);
        })
        .catch((error) => {
          if (cancelled) return;
          setInviteError(error?.response?.data?.error?.message || 'This tester invitation is invalid or no longer available.');
        })
        .finally(() => {
          if (!cancelled) setInviteLoading(false);
        });
    } else if (!location.state?.plan) {
      navigate('/pricing', { replace: true });
      return () => { cancelled = true; };
    } else {
      setSubscriptionType(location.state.plan);
    }

    checkLogin();

    if (emailParam && !inviteToken) {
      setNewEmail(emailParam);
    }

    const event = {
      event: {
        label: 'PageView',
        data: {
          page: 'Sign Up',
          email: emailParam || null,
          invitation: Boolean(inviteToken),
        }
      }

    }

    logEvent(event);

    return () => { cancelled = true; };
  }, [checkLogin, location.search, location.state, navigate]);


  return (
    <div>
      {orgMember ? navigate('/overview') :
        <div className='page-content auth-page'>
          <div className="auth-intro"><span>{compInviteToken ? 'Sayable tester program' : 'Start with Sayable'}</span><h1>A better first impression on every call.</h1><p>Set up a receptionist that understands your business and completes the work your callers need.</p><div className="auth-proof"><strong>{subscriptionType || (inviteLoading ? <LoadingIndicator label="Checking invitation…" /> : 'Your plan')}</strong><span>{compInviteToken ? 'Your subscription and all usage, including overages, are fully comped.' : 'You can review plan details before billing begins.'}</span></div></div>
          <div className="signup-selection auth-card">
            <div className='page-title'>Create your account</div>
            <p className="auth-card-subtitle">{compInviteToken ? 'Accept your private tester invitation. No payment method is required.' : 'A few details, then we’ll personalize your receptionist.'}</p>
            {inviteLoading ? <LoadingIndicator label="Validating your tester invitation…" /> : null}
            {inviteError ? <div className="signup-invite-error" role="alert"><strong>Invitation unavailable</strong><span>{inviteError}</span><button type="button" onClick={() => navigate('/pricing')}>View regular plans</button></div> : null}
            {compInvitation && !inviteError ? <div className="signup-invite-status is-valid"><strong>Comped tester account</strong><span>This single-use invitation includes the {compInvitation.subscriptionType} plan with all usage comped.</span></div> : null}
            {!inviteLoading && !inviteError && subscriptionType ? <GoogleSignInButton purpose="signup" divider
              disabled={buttonClicked} payload={{ subscriptionType, pricingVersion: location.state?.pricingVersion, compInviteToken: compInviteToken || undefined }}
              onSuccess={result => {
                if (result.token) login(result.token);
                else navigate(result.status === 'verify_email' ? '/verify-account' : '/pricing');
              }} /> : null}
            <form>

              <div className='signup-label'><span className='red'>*</span> Email <span className='label-note'>(required)</span></div>
              <input 
                  className='user-email' 
                  type="text" 
                  name="signup-email" 
                  id="signup-email"
                  value={newEmail} // Set the input value to newEmail
                  onChange={(e) => setNewEmail(e.target.value)} // Update newEmail on change
                  readOnly={Boolean(compInvitation?.email)}
                />

              <PasswordFields
                password={password}
                confirmPassword={verifyPassword}
                onPasswordChange={value => {
                  setPassword(value);
                  setPasswordError('');
                }}
                onConfirmPasswordChange={setVerifyPassword}
                userInputs={[newEmail]}
                disabled={buttonClicked}
                idPrefix="signup"
                labelClassName="signup-label"
                serverError={passwordError}
              />

              <div className='signup-label'><span className='red'>*</span> First Name <span className='label-note'>(required)</span></div>
              <input className='first-name' type="text" name="first-name" id="first-name" />

              <div className='signup-label'><span className='red'>*</span> Last Name <span className='label-note'>(required)</span></div>
              <input className='last-name' type="text" name="last-name" id="last-name" />

              <button className='button signup-button' onClick={handleSubmit} disabled={buttonClicked || inviteLoading || Boolean(inviteError)}><BusyContent busy={buttonClicked} label={"Creating account…"}>{'Continue'}</BusyContent></button>
            </form>

            {/* <div className="third-party-login-section">
            <GoogleLogin
              text="continue_with"
              onSuccess={handleGoogleSuccess}
              onFailure={handleGoogleFailure}
            />
          </div> */}
            <br />
            {/* <div className="signup-selection">
            <div ref={divRef} />
            </div> */}

          </div>

        </div>}

    </div>
  );
}

export default SignUp;
